/**
 * AbundanceNightJourneyPage - Program-Aligned 21 Night Subconscious Integration
 * 
 * Features:
 * - 3-phase structure (Opening & Safety, Identity & Worth, Embodiment)
 * - 10 affirmations per night with tapping interaction
 * - Sleep Screen Experience during audio playback
 * - Program-specific content aligned with Abundance Journey
 * 
 * Sleep Screen Design:
 * - Dark gradient background
 * - Minimal controls (fade out after 3-5 seconds)
 * - Soft affirmation display
 * - Gentle entry/exit transitions
 */
import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { BottomNav } from "../../components/BottomNav";
import {
  ChevronLeft, Moon, Check, Lock, Play, Pause,
  Volume2, Sparkles, Crown, ChevronRight
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Phase styling
const PHASE_CONFIG = {
  1: { 
    name: "Opening & Safety",
    color: "emerald",
    gradient: "from-emerald-900/90 to-teal-900/90"
  },
  2: { 
    name: "Identity & Worth",
    color: "violet",
    gradient: "from-violet-900/90 to-purple-900/90"
  },
  3: { 
    name: "Embodiment",
    color: "rose",
    gradient: "from-rose-900/90 to-pink-900/90"
  }
};

export const AbundanceNightJourneyPage = () => {
  const navigate = useNavigate();
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchJourney();
  }, []);

  const fetchJourney = async () => {
    const token = localStorage.getItem("reclaim_token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/integration/journey?program=abundance`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || "Failed to load journey");
      }

      const data = await response.json();
      setJourney(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStartNight = (nightNumber) => {
    navigate(`/journey/abundance/night/${nightNumber}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0a0a12] to-[#12121f] flex items-center justify-center">
        <motion.div
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="flex items-center gap-3 text-indigo-400/60"
        >
          <Moon className="w-5 h-5" />
          <span>Loading your night journey...</span>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0a0a12] to-[#12121f] p-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white/60 mb-6">
          <ChevronLeft size={20} /> Back
        </button>
        <div className="text-center py-12">
          <Lock className="w-16 h-16 text-indigo-400/50 mx-auto mb-4" />
          <h2 className="text-xl font-heading text-white mb-2">Access Required</h2>
          <p className="text-white/60">{error}</p>
        </div>
      </div>
    );
  }

  const completedCount = journey?.completed_count || 0;
  const totalNights = journey?.total_nights || 21;
  const progressPercent = Math.round((completedCount / totalNights) * 100);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0a12] to-[#12121f]">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-gradient-to-b from-[#0a0a12] via-[#0a0a12]/95 to-transparent pb-4">
        <div className="p-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/journey/abundance")}
            className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
          >
            <ChevronLeft size={20} className="text-white" />
          </button>
          <div className="flex items-center gap-2">
            <Moon className="w-5 h-5 text-indigo-400" />
            <span className="text-indigo-200/60 text-sm">21 Night Journey</span>
          </div>
        </div>
      </div>

      {/* Journey Header */}
      <div className="px-6 pb-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <motion.div
            animate={{ 
              boxShadow: ["0 0 40px rgba(99, 102, 241, 0.2)", "0 0 70px rgba(99, 102, 241, 0.35)", "0 0 40px rgba(99, 102, 241, 0.2)"]
            }}
            transition={{ duration: 4, repeat: Infinity }}
            className="w-20 h-20 rounded-full bg-gradient-to-br from-indigo-500/30 to-purple-600/20 
                      flex items-center justify-center mx-auto mb-4 border border-indigo-400/30"
          >
            <Moon className="w-10 h-10 text-indigo-400" />
          </motion.div>

          <h1 className="text-2xl font-heading text-white mb-2" data-testid="night-journey-title">
            {journey?.title}
          </h1>
          <p className="text-indigo-200/50 text-sm max-w-xs mx-auto">
            {journey?.subtitle}
          </p>
        </motion.div>

        {/* Progress Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/5 rounded-2xl p-5 mb-6 border border-indigo-500/10"
        >
          <div className="flex justify-between items-center mb-3">
            <span className="text-white font-medium">Your Progress</span>
            <span className="text-indigo-400 text-sm">
              {completedCount} of {totalNights} nights
            </span>
          </div>

          {/* Progress Bar */}
          <div className="h-3 bg-indigo-950/50 rounded-full overflow-hidden mb-4">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progressPercent}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full"
            />
          </div>

          {/* Phase Indicators */}
          <div className="flex justify-between text-xs">
            {journey?.phases?.map((phase) => (
              <div key={phase.phase} className="text-center">
                <p className="text-white/40">Phase {phase.phase}</p>
                <p className="text-indigo-400/60">{phase.name}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Nights by Phase */}
        {journey?.phases?.map((phase) => {
          const phaseNights = journey?.nights?.filter(n => n.phase === phase.phase) || [];
          const phaseConfig = PHASE_CONFIG[phase.phase];

          return (
            <motion.div
              key={phase.phase}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * phase.phase }}
              className="mb-6"
            >
              {/* Phase Header */}
              <div className="flex items-center gap-3 mb-3 px-1">
                <div className={`w-2 h-2 rounded-full bg-${phaseConfig.color}-500`} />
                <h2 className="text-white/80 text-sm font-medium uppercase tracking-wider">
                  {phase.name}
                </h2>
                <span className="text-white/30 text-xs">Nights {phase.nights}</span>
              </div>

              {/* Nights Grid */}
              <div className="grid grid-cols-7 gap-2">
                {phaseNights.map((night) => (
                  <NightCard
                    key={night.night}
                    night={night}
                    phaseConfig={phaseConfig}
                    onStart={() => handleStartNight(night.night)}
                  />
                ))}
              </div>
            </motion.div>
          );
        })}

        {/* Back to Day Journey Link */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-8"
        >
          <button
            onClick={() => navigate("/journey/abundance")}
            className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 
                     rounded-xl border border-emerald-500/20 hover:border-emerald-400/40 transition-all"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-emerald-400" />
              </div>
              <div className="text-left">
                <p className="text-white font-medium text-sm">Abundance Journey</p>
                <p className="text-emerald-200/50 text-xs">Continue your daily transformation</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-emerald-400" />
          </button>
        </motion.div>
      </div>

      {/* Bottom Padding */}
      <div className="h-24" />
      <BottomNav />
    </div>
  );
};


/**
 * Night Card - Compact night indicator
 */
const NightCard = ({ night, phaseConfig, onStart }) => {
  const isLocked = !night.is_unlocked;
  const isCompleted = night.is_completed;

  return (
    <motion.button
      whileHover={!isLocked ? { scale: 1.05 } : {}}
      whileTap={!isLocked ? { scale: 0.95 } : {}}
      onClick={() => !isLocked && onStart()}
      disabled={isLocked}
      className={`
        aspect-square rounded-xl flex items-center justify-center transition-all
        ${isLocked 
          ? "bg-white/5 cursor-not-allowed opacity-40" 
          : isCompleted
            ? `bg-${phaseConfig.color}-500/20 border border-${phaseConfig.color}-500/30`
            : "bg-white/5 hover:bg-white/10 border border-transparent hover:border-indigo-500/30"
        }
      `}
      data-testid={`night-card-${night.night}`}
      title={night.theme}
    >
      {isCompleted ? (
        <Check className={`w-4 h-4 text-${phaseConfig.color}-400`} />
      ) : isLocked ? (
        <Lock className="w-3 h-3 text-white/20" />
      ) : (
        <span className="text-white/80 text-sm font-medium">{night.night}</span>
      )}
    </motion.button>
  );
};


export default AbundanceNightJourneyPage;
